/**
 * Pure Web-Vitals → PostHog telemetry helper.
 *
 * Pinned by edv-6.3 (2026-05-15) so a future RUM dashboard or alert
 * can compare LCP/INP/CLS P75 against the targets recorded in
 * `services/webapp-foltrigg/CLAUDE.md` (PERFORMANCE BASELINES, Web
 * Vitals table): LCP P75 <= 2500 ms, INP P75 <= 200 ms, CLS P75 <= 0.1,
 * Google CWV "Good" public targets per https://web.dev/articles/vitals.
 *
 * --------------------------------------------------------------------
 *  Why a separate module from `app/providers.tsx`'s posthog-js init
 * --------------------------------------------------------------------
 *
 * The provenance feature locks its PostHog allow-list inside
 * `components/evidence/shared/provenance-telemetry.ts` (4 events;
 * adding a fifth requires architectural review + an updated
 * `provenance-telemetry.test.ts` allow-list assertion). Web Vitals
 * is a globally-scoped concern — emitted from `app/layout.tsx` for
 * every route including unauthenticated `/login`, `/forgot-password`,
 * etc. — and would lie outside that allow-list's scope, so wedging
 * `webapp.web_vital` through `provenance-telemetry.ts` would conflate
 * two independent telemetry workstreams. The webapp's
 * `eslint.config.mjs::no-restricted-imports` rule that bans
 * `posthog-js` inside `components/evidence/**` deliberately does NOT
 * cover `lib/telemetry/**` (or `app/web-vitals.tsx`) for exactly
 * this reason.
 *
 * --------------------------------------------------------------------
 *  Singleton wiring (edv-6.3 code-review pass-1 H1 fix, 2026-05-15)
 * --------------------------------------------------------------------
 *
 * `posthog-js` v1.x consumed as an npm module exposes the analytics
 * client ONLY through the module-scoped default export — it does NOT
 * attach itself to `window.posthog` / `globalThis.posthog`. The
 * window-global pattern only happens with the legacy `<script>`
 * snippet load method, which this codebase does not use (verified
 * by ripgrep against `node_modules/posthog-js/dist/{module,main}.js`
 * — zero matches for `window.posthog` / `globalThis.posthog`).
 *
 * Therefore production callers MUST `import posthog from 'posthog-js'`
 * directly (see `app/web-vitals.tsx`) and pass `posthog.capture`
 * bound to the singleton. The previous attempt (pre-pass-1) read
 * `globalThis.posthog`, which was always `undefined`, so every
 * metric was silently dropped. Tests passed because they synthesized
 * the global themselves; the production wiring was broken.
 *
 * The helper itself stays parameterized on `posthogCapture` so
 * tests can inject a `vi.fn()` without importing `posthog-js` (the
 * tests already exercised this pattern correctly).
 *
 * --------------------------------------------------------------------
 *  CJIS payload-allow-list (PE-10 / no-CJI-in-payloads)
 * --------------------------------------------------------------------
 *
 * The Web-Vitals `Metric` object (next/web-vitals → web-vitals v4)
 * carries `{name, value, rating, delta, entries, id, navigationType}`.
 * We DESTRUCTURE the safe-field subset
 * `{name, value, rating, navigationType, id}` — `entries` and
 * `delta` are deliberately dropped because:
 *   - `entries` is an array of `PerformanceEventTiming` /
 *     `PerformanceLayoutShift` / `PerformanceLargestContentfulPaint`
 *     entries that may carry DOM `target` references and CSS
 *     selectors. These could expose page structure (e.g. a
 *     `[data-case-id="..."]` selector). Hard-dropping the field
 *     means no future regression can leak them through the
 *     telemetry path.
 *   - `delta` is internal to the web-vitals reporter's
 *     incremental-update pattern and adds no analytical value at
 *     the dashboard layer.
 * No URL, no case ID, no defendant name, no audio filename can
 * leak — the only field that could carry case-relevant data is
 * `id` (an opaque `v4-<timestamp>-<random>` correlation token
 * issued by `web-vitals` v4 — see
 * `node_modules/next/dist/compiled/web-vitals/web-vitals.js` line ~16).
 *
 * The destructure is the structural enforcement; the
 * `__tests__/lib/telemetry/web-vitals.test.ts::"payload allow-list"`
 * suite locks the field set so any future regression that adds
 * a forbidden key to the `event_properties` arg fires a test
 * failure.
 *
 * --------------------------------------------------------------------
 *  Dev verification path (edv-6.3 code-review pass-1 M3 fix, 2026-05-15)
 * --------------------------------------------------------------------
 *
 * `app/providers.tsx::posthog.init(...).loaded` calls
 * `posthog.opt_out_capturing()` in `NODE_ENV === 'development'` to
 * avoid noisy dev-time PostHog ingestion. That means a developer
 * running `npm run dev` and filtering DevTools Network on
 * `i.posthog.com` sees ZERO events for `webapp.web_vital` even
 * when the wiring is correct — the singleton is opted out before
 * any capture call lands. To give the operator a signal in dev,
 * `reportWebVital` ALSO emits a `console.debug` line containing the
 * allow-listed payload when `process.env.NODE_ENV === 'development'`.
 * The dev sink is structurally identical to the PostHog payload
 * (same destructure, same shape) so what you see in the dev console
 * is exactly what hits PostHog in prod.
 *
 * --------------------------------------------------------------------
 *  Dev defense-in-depth alarm (edv-6.3 code-review pass-2 H1 fix, 2026-05-15)
 * --------------------------------------------------------------------
 *
 * AC4(e) of `edv-6.3` requires a Dev-only `console.warn` when a
 * single sample's `rating === 'poor'` for any of the three pinned
 * metrics (LCP / INP / CLS). The pass-1 M3 fix added a verbose
 * `console.debug` sink for operator verification but inadvertently
 * dropped the poor-rating warn channel. Pass-2 restores the warn
 * alongside the debug — the two coexist:
 *   - `console.debug` fires on every metric in dev (operator
 *     verification: "the reporter mounted and is firing").
 *   - `console.warn` fires only when `rating === 'poor'` AND the
 *     metric is one of LCP / INP / CLS (defense-in-depth: "a
 *     pinned metric just regressed past the Google CWV 'Good'
 *     threshold on the dev's machine"). FCP / TTFB are also
 *     emitted by `useReportWebVitals` but are NOT pinned by this
 *     story, so they do not trigger the warn.
 *
 * `PINNED_METRICS` below is the source of truth for which metric
 * names trigger the warn. Adding a fourth pinned metric (e.g., if
 * Google promotes TTFB as a Core Web Vital in a future release)
 * is a single-line addition here plus a row in the CLAUDE.md Web
 * Vitals table.
 */

/**
 * Subset of next/web-vitals' `Metric` we accept. Extra fields on
 * the runtime object are explicitly ignored (the destructure in
 * `reportWebVital` enforces the allow-list).
 *
 * `name` is one of the six metrics next/web-vitals emits today —
 * 'TTFB', 'FCP', 'LCP', 'FID' (legacy / deprecated by Google
 * 2024-03; pinned NOT-tracked per DECISIONS.md 2026-05-15), 'CLS',
 * 'INP'. The widened `string` is a defensive: if next/web-vitals
 * adds a new metric in a future minor (e.g. 'TTI'), we forward it
 * to PostHog rather than silently swallowing it.
 *
 * NOT re-using `next/web-vitals`'s `Metric` type because Next.js 16
 * does not re-export it from the public `next/web-vitals` entry
 * (it's only exposed via `next/dist/compiled/web-vitals` which is
 * an internal path). We define our own structural shape — the
 * callback signature in `app/web-vitals.tsx` (`(metric: WebVitalsMetricInput) => void`)
 * is structurally compatible with what `useReportWebVitals` passes
 * because TypeScript widens the parameter on assignment.
 *
 * The index signature is wider on purpose so test fixtures can
 * inject forbidden fields and the destructure-strip behavior in
 * `buildWebVitalPayload` is observable.
 */
export interface WebVitalsMetricInput {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  navigationType?: string;
  id: string;
  // Forbidden keys (`entries`, `delta`, plus anything else upstream
  // adds) are accepted at the type level via the index signature
  // but stripped at the destructure in `reportWebVital`.
  [extra: string]: unknown;
}

/**
 * Capture function shape — matches `posthog-js`'s `capture` method
 * (https://posthog.com/docs/libraries/js#capture). Typed structurally
 * so the test suite can pass a `vi.fn()` without importing
 * `posthog-js`.
 */
export type PosthogCapture = (
  event: string,
  properties: Record<string, unknown>
) => void;

export const WEB_VITAL_EVENT_NAME = 'webapp.web_vital';

/**
 * Metric names pinned in the CLAUDE.md Web Vitals table (LCP, INP,
 * CLS). A `rating === 'poor'` sample on one of these in dev
 * triggers the AC4(e) defense-in-depth `console.warn`. FCP / TTFB are
 * forwarded to PostHog (allow-list permits all metrics) but do NOT
 * trigger the warn because they are not pinned by this story.
 */
export const PINNED_METRICS = ['LCP', 'INP', 'CLS'] as const;

/**
 * Build the allow-listed payload from a Web Vitals metric. Pure;
 * exported so the dev console.debug sink and the PostHog forward
 * can share the same destructure (single source of truth for what
 * we send anywhere).
 */
export function buildWebVitalPayload(
  metric: WebVitalsMetricInput
): Record<string, unknown> {
  // Structural allow-list — see CJIS-payload-allow-list block in the
  // module header. ANY new field on `metric` is dropped here; only
  // the five explicit destructure targets are forwarded.
  const { name, value, rating, navigationType, id } = metric;
  return {
    metric_name: name,
    metric_value: value,
    metric_rating: rating,
    navigation_type: navigationType ?? null,
    metric_id: id,
  };
}

/**
 * Emit a single Web-Vitals metric to PostHog (and to console.debug
 * in development — see "Dev verification path" in the module header).
 *
 * Designed to be called from a `useReportWebVitals` callback per
 * Next.js's contract:
 *   useReportWebVitals((metric) => reportWebVital(metric, posthog.capture.bind(posthog)));
 *
 * `posthogCapture` is parameterized so the test suite can inject
 * a spy. Production code (`app/web-vitals.tsx`) passes
 * `posthog.capture.bind(posthog)` from the imported
 * `posthog-js` singleton.
 *
 * The function is INTENTIONALLY a no-op for the PostHog forward
 * when `posthogCapture` is null/undefined (defensive against
 * callers that conditionally enable telemetry). The dev console
 * sink fires UNCONDITIONALLY on `NODE_ENV === 'development'`, even
 * when posthog is unavailable, so the operator can still verify
 * that the reporter mounted and is firing.
 */
export function reportWebVital(
  metric: WebVitalsMetricInput,
  posthogCapture: PosthogCapture | null | undefined
): void {
  const payload = buildWebVitalPayload(metric);

  // Dev sinks — fire regardless of posthogCapture. See module header
  // "Dev verification path" + "Dev defense-in-depth alarm" for why:
  // providers.tsx opts posthog out of capturing in
  // NODE_ENV === 'development', so without these sinks the operator
  // has no signal during `npm run dev` smoke testing.
  if (
    typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'development'
  ) {
    // Verbose verification sink — every metric, every rating.
    console.debug(`[web-vitals] ${WEB_VITAL_EVENT_NAME}`, payload);

    // Defense-in-depth alarm (AC4(e)) — only poor LCP/INP/CLS.
    // `console.warn` is the alarm level the AC text mandates so
    // the regression is visible in default DevTools without
    // toggling verbose logging.
    if (
      metric.rating === 'poor' &&
      (PINNED_METRICS as readonly string[]).includes(metric.name)
    ) {
      console.warn(
        `[web-vitals] Poor ${metric.name} sample (${metric.value}) — exceeds Google CWV "Good" target (see CLAUDE.md PERFORMANCE BASELINES).`,
        payload
      );
    }
  }

  if (!posthogCapture) return;
  posthogCapture(WEB_VITAL_EVENT_NAME, payload);
}
